import React, { useState, useEffect, useRef, forwardRef } from 'react';
import { ICONS } from 'Utils/icons';

export const DocumentTemplateToc = forwardRef((props, ref) => {
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [activeSection, setActiveSection] = useState('');
  const [isTopNavExpanded, setIsTopNavExpanded] = useState(false);
  const [isCollapsing, setIsCollapsing] = useState(false);
  const topNavRef = useRef(null);
  const data = props.data.commonFormatsData;
  const isSideNav = props.data.isSideNav;
  const extraSections = props.data.extraSections ?? [];

  useEffect(() => {
    const initialExpanded = new Set();
    data.commonFormatsData.forEach((item, index) => {
      if (item.children && item.children.length) {
        const l1Key = `level1-${index}`;
        initialExpanded.add(l1Key);
        item.children.forEach((child, j) => {
          if (child.children && child.children.length) {
            initialExpanded.add(`${l1Key}-level2-${j}`);
          }
        });
      }
    });
    setExpandedItems(initialExpanded);
  }, [data.commonFormatsData]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 60;
      let closestSection = '';
      let closestDistance = Infinity;

      for (const [key, iref] of Object.entries(ref.current)) {
        if (iref) {
          const distance = Math.abs(iref.offsetTop - scrollPosition);
          if (iref.offsetTop <= scrollPosition && distance < closestDistance) {
            closestDistance = distance;
            closestSection = key;
          }
        }
      }

      // Check extraSections refs
      for (const { title, ref: xRef } of extraSections) {
        if (xRef?.current) {
          const distance = Math.abs(xRef.current.offsetTop - scrollPosition);
          if (xRef.current.offsetTop <= scrollPosition && distance < closestDistance) {
            closestDistance = distance;
            closestSection = `extra-${title}`;
          }
        }
      }

      if (closestSection) setActiveSection(closestSection);
    };
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [ref, extraSections]);

  useEffect(() => {
    if (!isSideNav && isTopNavExpanded && !isCollapsing) {
      const handleClickOutside = (event) => {
        if (topNavRef.current && !topNavRef.current.contains(event.target)) {
          handleTopNavCollapse();
        }
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isSideNav, isTopNavExpanded, isCollapsing]);

  const handleTopNavCollapse = () => {
    setIsCollapsing(true);
    setTimeout(() => { setIsTopNavExpanded(false); setIsCollapsing(false); }, 30);
  };

  const toggleExpand = (key, e) => {
    e.stopPropagation();
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(key)) newSet.delete(key);
      else newSet.add(key);
      return newSet;
    });
  };

  const scrollToSection = (key) => {
    if (!isSideNav) {
      handleTopNavCollapse();
      setTimeout(() => {
        const element = ref.current[key];
        if (element) {
          const offsetPosition = element.getBoundingClientRect().top + window.scrollY + 25 - 60 - 20;
          window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
        }
      }, 32);
    } else {
      const element = ref.current[key];
      if (element) element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    document.activeElement?.blur();
  };

  const scrollToExtra = (xRef) => {
    if (!isSideNav) handleTopNavCollapse();
    setTimeout(() => {
      xRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, isSideNav ? 0 : 32);
    document.activeElement?.blur();
  };

  const getActiveTitle = () => {
    const findTitle = (items, parentKey = '') => {
      for (let index = 0; index < items.length; index++) {
        const item = items[index];
        const key = `${parentKey}level${parentKey ? items[0].level || 2 : 1}-${index}`;
        if (key === activeSection && item.title) return item.title;
        if (item.children) {
          const childTitle = findTitle(item.children, key + '-');
          if (childTitle) return childTitle;
        }
      }
      return null;
    };

    const mainTitle = findTitle(data.commonFormatsData);
    if (mainTitle) return mainTitle;

    const activeExtra = extraSections.find(({ title }) => activeSection === `extra-${title}`);
    if (activeExtra) return activeExtra.title;

    return 'Table of Contents';
  };

  const renderNavItem = (item, index, level, lastLevel1Item, parentKey = '', parentHasNoTitle = false) => {
    const key = `${parentKey}level${level}-${index}`;
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedItems.has(key);
    const isActive = activeSection === key;

    if (level === 1 && !item.title && hasChildren) {
      return item.children.map((child, childIndex) =>
        renderNavItem(child, childIndex, 2, lastLevel1Item, key + '-', true)
      );
    }

    let paddingStart = 0;
    if (level === 1) paddingStart = 1;
    else if (level === 2 && parentHasNoTitle) paddingStart = 1;
    else if (level === 2) paddingStart = 2;
    else if (level === 3) paddingStart = 3;

    return [
      <div
        key={key}
        className={`pt-2 pb-2 sidetoc-nav-item ps-${paddingStart} ${isActive ? 'sidetoc-active-section' : 'sidetoc-inactive-section'} ${level === 1 || level === 2 ? 'common-formats-header' : ''}`}
      >
        <div className="d-flex align-items-start justify-content-between">
          <span
            onClick={() => scrollToSection(key)}
            onKeyDown={(e) => (e.key === 'Enter' ? scrollToSection(key, e) : () => {})}
            style={{ flex: 1, cursor: 'pointer' }}
            tabIndex={0}
            className="body"
          >
            {item.title}
          </span>
          {hasChildren && (
            <span
              onClick={(e) => toggleExpand(key, e)}
              onKeyDown={(e) => (e.key === 'Enter' ? toggleExpand(key, e) : () => {})}
              style={{ cursor: 'pointer' }}
              tabIndex={0}
            >
              {isExpanded ? <ICONS.downArrow size={16} /> : <ICONS.rightArrow size={16} />}
            </span>
          )}
        </div>
      </div>,

      ...(hasChildren && isExpanded ? item.children.flatMap((child, childIndex) =>
        renderNavItem(child, childIndex, level + 1, lastLevel1Item, key + '-', false)
      ) : []),

      ...(level === 1 && index !== lastLevel1Item - 1
        ? [<div key={`${key}-border`} className="mt-2 mb-2 common-formats-border-bottom-dashed" />]
        : []),
    ];
  };

  const renderExtraSections = () =>
    extraSections.map(({ title, ref: xRef }) => {
      const isActive = activeSection === `extra-${title}`;
      return (
        <React.Fragment key={`extra-${title}`}>
          <div className="mt-2 mb-2 common-formats-border-bottom-dashed" />
          <div
            className={`pt-2 pb-2 sidetoc-nav-item ps-1 ${isActive ? 'sidetoc-active-section' : 'sidetoc-inactive-section'} common-formats-header`}
          >
            <span
              onClick={() => scrollToExtra(xRef)}
              onKeyDown={(e) => { if (e.key === 'Enter') scrollToExtra(xRef); }}
              style={{ flex: 1, cursor: 'pointer' }}
              tabIndex={0}
              className="body"
            >
              {title}
            </span>
          </div>
        </React.Fragment>
      );
    });

  if (isSideNav) {
    return (
      <div className="sidetoc-container">
        <h2 className="mb-3 common-formats-header toc-header header4">Table of Contents</h2>
        <div className="sidetoc-menu pb-4 pe-2 pt-1">
          <div className="d-flex flex-column align-items-start" style={{ width: '100%' }}>
            {data.commonFormatsData.map((item, index) =>
              renderNavItem(item, index, 1, data.commonFormatsData.length)
            )}
            {renderExtraSections()}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div ref={topNavRef} className={`toptoc-container ${isTopNavExpanded && !isCollapsing ? 'expanded' : ' '}`}>
      <div
        className="d-flex align-items-center common-formats-header toc-header justify-content-between p-2 ps-4"
        style={{ cursor: 'pointer' }}
        onClick={() => { isTopNavExpanded ? handleTopNavCollapse() : setIsTopNavExpanded(true); }}
      >
        <span className="header4 pe-3">{getActiveTitle()}</span>
        {isTopNavExpanded ? <ICONS.upArrow size={20} /> : <ICONS.downArrow size={20} />}
      </div>
      {isTopNavExpanded && !isCollapsing && (
        <div className={`p-3 m-1 toptoc-nav-container ${isTopNavExpanded && !isCollapsing ? 'expanded' : ' '}`}>
          <div className="d-flex flex-column align-items-start" style={{ width: '100%' }}>
            {data.commonFormatsData.map((item, index) =>
              renderNavItem(item, index, 1, data.commonFormatsData.length)
            )}
            {renderExtraSections()}
          </div>
        </div>
      )}
    </div>
  );
});
